import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Heart, MapPin, Menu, Phone, ShoppingBag, User, Scissors, X, Check } from "lucide-react";
import { BRAND, OFFERS } from "../data/mock";
import { BREEDS, CATEGORIES, FEATURED_BRANDS, MENU, shopLink } from "../data/shopMenu";
import { useCart, inr, useStoreBrands } from "../store/CartContext";
import { useAccount } from "../store/AccountContext";
import SearchBox from "./store/SearchBox";
import MobileMenu from "./MobileMenu";

export const GROOMING_LINKS = [
  ["Grooming & Spa home", "/grooming", "Our studio, process and reviews"],
  ["Services", "/services", "Bath, haircut, spa & home grooming"],
  ["Pricing", "/pricing", "Breed-wise grooming price list"],
  ["Offers", "/pricing#offers", "Pick 1 free treat per booking"],
  ["Gallery", "/gallery", "Before & after transformations"],
  ["Book appointment", "/contact", "Store or doorstep slots"],
];

function PincodePicker() {
  const [pin, setPin] = useState(() => localStorage.getItem("bf_pin") || "");
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(pin);
  const ok = /^[1-9][0-9]{5}$/.test(val);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid="header-pincode-button" className="flex items-center gap-1.5 text-white/85 hover:text-white">
        <MapPin className="h-3.5 w-3.5 text-[#FC5106]" /> {pin ? <>Deliver to <b>{pin}</b></> : "Enter delivery pincode"} <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute left-0 top-7 z-[80] w-64 rounded-2xl bg-white p-4 text-[#1E0A5C] shadow-2xl" data-testid="header-pincode-popover">
          <p className="mb-2 text-sm font-bold">Check delivery</p>
          <div className="flex gap-2">
            <input value={val} onChange={(e) => setVal(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="6-digit pincode" data-testid="header-pincode-input" className="w-full rounded-xl border border-[#3915AA]/15 px-3 py-2 text-sm outline-none focus:border-[#FC5106]" />
            <button disabled={!ok} onClick={() => { localStorage.setItem("bf_pin", val); setPin(val); setTimeout(() => setOpen(false), 1200); }} data-testid="header-pincode-save" className="rounded-xl bg-[#3915AA] px-3 text-sm font-bold text-white disabled:opacity-40">Check</button>
          </div>
          {pin && pin === val && <p className="mt-2 flex items-center gap-1 text-xs text-green-600" data-testid="header-pincode-result"><Check className="h-3.5 w-3.5" /> {pin.startsWith("75") ? "Express delivery in 1–3 days" : "We deliver here in 4–7 days"}</p>}
        </div>
      )}
    </div>
  );
}

function RotatingOffer() {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % OFFERS.length), 3500); return () => clearInterval(t); }, []);
  const o = OFFERS[i];
  return <Link key={i} to="/pricing#offers" data-testid="promo-bar-offer" className="promo-swap truncate text-white/90 hover:text-white"><b className="text-[#FFB08A]">{o.tag}</b> {o.title} — {o.detail}</Link>;
}

export function BrandTile({ b, live, onClick, className = "" }) {
  if (!live) return <span data-testid={`brand-soon-${b.replace(/\W+/g, "-")}`} className={`flex flex-col items-center justify-center rounded-xl border border-dashed border-[#3915AA]/15 bg-white px-2 text-center font-heading text-sm font-bold text-[#3915AA]/40 ${className}`}>{b}<span className="text-[9px] font-semibold uppercase tracking-wider">Coming soon</span></span>;
  return <Link to={`/shop/all?brand=${encodeURIComponent(b)}`} onClick={onClick} data-testid={`brand-tile-${b.replace(/\W+/g, "-")}`} className={`flex items-center justify-center rounded-xl border border-[#3915AA]/10 bg-[#F7F5FF] px-2 text-center font-heading text-sm font-bold text-[#3915AA] hover:border-[#FC5106] hover:text-[#FC5106] ${className}`}>{b}</Link>;
}

function MegaPanel({ k, close }) {
  const live = useStoreBrands() || [];
  if (k === "Dog" || k === "Cat") {
    return (
      <div className="grid grid-cols-5 gap-x-6 gap-y-5">
        {MENU[k].map(([cat, subs]) => (
          <div key={cat}>
            <Link to={shopLink(k, cat)} onClick={close} className="mb-2 block text-sm font-bold text-[#3915AA] hover:text-[#FC5106]">{CATEGORIES[cat]}</Link>
            <ul className="space-y-1.5">{subs.map((s) => <li key={s}><Link to={shopLink(k, cat, s)} onClick={close} data-testid={`mega-${k}-${cat}-${s}`.replace(/[^a-zA-Z0-9-]/g, "")} className="text-[13px] text-[#5E5675] hover:text-[#FC5106]">{s}</Link></li>)}</ul>
          </div>
        ))}
        <Link to={`/shop/all?pet=${k}`} onClick={close} className="col-span-1 flex items-end rounded-2xl bg-[#3915AA] p-4 text-sm font-bold text-white hover:bg-[#2E0F94]">Shop all {k === "Dog" ? "dog" : "cat"} products →</Link>
      </div>
    );
  }
  if (k === "Brands") {
    return (
      <div>
        <div className="grid grid-cols-7 gap-3">
          {[...new Set([...live, ...FEATURED_BRANDS])].map((b) => <BrandTile key={b} b={b} live={live.includes(b)} onClick={close} className="h-16" />)}
        </div>
        <Link to="/brands" onClick={close} data-testid="mega-view-all-brands" className="mt-4 inline-block text-sm font-bold text-[#FC5106]">View all brands →</Link>
      </div>
    );
  }
  if (k === "Breeds") {
    return (
      <div>
        <div className="grid grid-cols-8 gap-3">
          {BREEDS.map((b) => (
            <Link key={b.slug} to={`/breeds/${b.slug}`} onClick={close} className="group text-center">
              <img src={b.img} alt={b.name} className="mx-auto aspect-square w-full rounded-2xl object-cover transition-transform group-hover:scale-105" />
              <p className="mt-2 text-xs font-bold text-[#1E0A5C] group-hover:text-[#FC5106]">{b.name}</p>
            </Link>
          ))}
        </div>
        <Link to="/breeds" onClick={close} className="mt-4 inline-block text-sm font-bold text-[#FC5106]">All breeds →</Link>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-4 gap-4">
      <div className="col-span-3 grid grid-cols-3 gap-3">
        {GROOMING_LINKS.map(([l, to, d]) => <Link key={l} to={to} onClick={close} className="rounded-xl p-3 hover:bg-[#F7F5FF]"><p className="text-sm font-bold text-[#3915AA]">{l}</p><p className="text-xs text-[#5E5675]">{d}</p></Link>)}
      </div>
      <Link to="/pricing#offers" onClick={close} className="rounded-2xl bg-[#FC5106] p-5 text-white"><p className="text-xs font-bold uppercase tracking-wider">Every booking</p><p className="mt-1 font-heading text-lg font-bold leading-tight">Pick 1 FREE treat: oil massage, toy pack or dematting</p></Link>
    </div>
  );
}

const MAIN = [["Dog", "Dogs"], ["Cat", "Cats"], ["Brands", "Brands"], ["Breeds", "Shop by Breed"], ["Grooming", "Grooming & Spa"]];

export default function SiteHeader() {
  const liveBrands = useStoreBrands() || [];
  const { count, subtotal, setOpen: openCart } = useCart();
  const { user, wishlist } = useAccount();
  const [mega, setMega] = useState(null);
  const [mobile, setMobile] = useState(false);
  const { pathname, search } = useLocation();
  useEffect(() => { setMega(null); setMobile(false); }, [pathname]);
  const Icon = ({ to, label, children, testid, badge }) => (
    <Link to={to} aria-label={label} data-testid={testid} className="relative rounded-full p-2 text-white hover:bg-white/10">{children}{badge > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#FC5106] px-1 text-[9px] font-bold">{badge}</span>}</Link>
  );

  return (
    <header data-testid="site-navbar" className="fixed inset-x-0 top-0 z-50 shadow-[0_8px_30px_-12px_rgba(20,6,70,0.5)]">
      <div className="bg-[#240A80] text-[11px] sm:text-xs">
        <div className="container-custom flex h-9 items-center justify-between gap-4">
          <PincodePicker />
          <div className="hidden min-w-0 flex-1 justify-center md:flex"><RotatingOffer /></div>
          <div className="flex items-center gap-4 text-white/80">
            <a href={`tel:+${BRAND.phoneRaw}`} data-testid="header-support-phone" className="hidden items-center gap-1.5 hover:text-white sm:flex"><Phone className="h-3.5 w-3.5" /> {BRAND.phone}</a>
            <Link to="/track-order" className="hover:text-white">Track order</Link>
          </div>
        </div>
      </div>

      <div className="bg-[#3915AA]">
        <div className="container-custom flex h-16 items-center gap-3 lg:gap-6">
          <button onClick={() => setMobile(true)} data-testid="navbar-mobile-toggle" aria-label="Open menu" className="-ml-1 rounded-full p-2 text-white hover:bg-white/10 lg:hidden"><Menu className="h-5 w-5" /></button>
          <Link to="/" data-testid="navbar-logo" className="flex-shrink-0"><img src={BRAND.logoWhiteUrl} alt="BarknFurs" className="h-7 w-auto sm:h-9" /></Link>
          <SearchBox className="hidden flex-1 md:block" />
          <div className="ml-auto flex items-center gap-0.5 sm:gap-1 md:ml-0">
            <div className="group relative hidden xl:block">
              <button data-testid="header-breed-brand-selector" className="flex items-center gap-1 rounded-full px-3 py-2 text-sm font-semibold text-white hover:bg-white/10">Brand / Breed <ChevronDown className="h-3.5 w-3.5" /></button>
              <div className="invisible absolute right-0 top-full z-[80] w-72 rounded-2xl bg-white p-3 text-[#1E0A5C] opacity-0 shadow-2xl transition-opacity group-hover:visible group-hover:opacity-100">
                <p className="px-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#5E5675]">Breeds</p>
                <div className="grid grid-cols-2">{BREEDS.map((b) => <Link key={b.slug} to={`/breeds/${b.slug}`} className="rounded-lg px-2 py-1.5 text-xs font-semibold hover:bg-[#F7F5FF]">{b.name}</Link>)}</div>
                <p className="px-2 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wider text-[#5E5675]">Brands</p>
                <div className="grid grid-cols-2">{liveBrands.slice(0, 8).map((b) => <Link key={b} to={`/shop/all?brand=${encodeURIComponent(b)}`} className="rounded-lg px-2 py-1.5 text-xs font-semibold hover:bg-[#F7F5FF]">{b}</Link>)}</div>
              </div>
            </div>
            <Link to="/contact" data-testid="navbar-book-cta" className="mr-1 hidden items-center gap-1.5 rounded-full bg-[#FC5106] px-4 py-2 text-sm font-bold text-white hover:bg-[#E24804] md:inline-flex"><Scissors className="h-4 w-4" /> Book Grooming</Link>
            <Icon to="/account" label="Account" testid="header-account-button">{user?.picture ? <img src={user.picture} alt="" className="h-5 w-5 rounded-full" /> : <User className="h-5 w-5" />}</Icon>
            <Icon to="/account?tab=wishlist" label="Wishlist" testid="header-wishlist-button" badge={wishlist.length}><Heart className="h-5 w-5" /></Icon>
            <button onClick={() => openCart(true)} data-testid="nav-cart-button" aria-label="Open cart" className="flex items-center gap-2 rounded-full p-2 text-white hover:bg-white/10 sm:pr-3">
              <span className="relative"><ShoppingBag className="h-5 w-5" />{count > 0 && <span data-testid="nav-cart-count" className="absolute -right-2 -top-2 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#FC5106] px-1 text-[9px] font-bold">{count}</span>}</span>
              <span className="hidden text-left leading-tight sm:block"><span className="block text-[10px] text-white/60">Cart</span><span data-testid="nav-cart-subtotal" className="block text-xs font-bold">{inr(subtotal)}</span></span>
            </button>
          </div>
        </div>
        <div className="container-custom pb-3 md:hidden"><SearchBox /></div>
      </div>

      <nav className="relative hidden border-b border-[#3915AA]/10 bg-white lg:block" onMouseLeave={() => setMega(null)}>
        <div className="container-custom flex h-11 items-center gap-1">
          {MAIN.map(([k, l]) => (
            <button key={k} onMouseEnter={() => setMega(k)} onClick={() => setMega((m) => (m === k ? null : k))} data-testid={`nav-mega-${k.toLowerCase()}`}
              className={`flex h-full items-center gap-1 px-3 text-sm font-bold transition-colors ${mega === k ? "text-[#FC5106] shadow-[inset_0_-3px_0_#FC5106]" : "text-[#1E0A5C] hover:text-[#FC5106]"}`}>
              {l} <ChevronDown className="h-3.5 w-3.5" />
            </button>
          ))}
          <Link to="/shop/all?sort=discount" onMouseEnter={() => setMega(null)} data-testid="nav-link-flash-deals" className={`flex h-full items-center px-3 text-sm font-bold ${search.includes("sort=discount") ? "text-[#FC5106]" : "text-[#1E0A5C] hover:text-[#FC5106]"}`}>Flash Deals</Link>
          {[["Blog", "/blog"], ["About", "/about"], ["Contact", "/contact"]].map(([l, to]) => (
            <NavLink key={l} to={to} onMouseEnter={() => setMega(null)} data-testid={`nav-link-${l.toLowerCase()}`} className={({ isActive }) => `flex h-full items-center px-3 text-sm font-bold ${isActive ? "text-[#FC5106]" : "text-[#1E0A5C] hover:text-[#FC5106]"}`}>{l}</NavLink>
          ))}
        </div>
        {mega && (
          <div data-testid={`mega-panel-${mega.toLowerCase()}`} className="absolute inset-x-0 top-full border-t border-[#3915AA]/10 bg-white shadow-2xl">
            <div className="container-custom max-h-[70vh] overflow-y-auto py-6"><MegaPanel k={mega} close={() => setMega(null)} /></div>
          </div>
        )}
      </nav>
      <MobileMenu open={mobile} onClose={() => setMobile(false)} />
    </header>
  );
}
